/**
 * AIProviderForm — shared "Add / Edit AI Provider" form.
 *
 * Replaces what was previously a 110-line duplicated form rendered inline
 * in both:
 *   - src/routes/organization/AISettings.tsx (org-scoped)
 *   - src/routes/admin/PlatformAISettings.tsx (super-admin platform)
 *
 * Key behaviour:
 *   - Loads model options DYNAMICALLY from /api/ai-config/models/{provider}
 *     (same endpoint super-admin was using). Falls back to the static
 *     `providers[i].knownModels` list if the dynamic fetch fails or returns
 *     empty — so the form keeps working even when a provider's model API
 *     is unreachable.
 *   - Supports both 'platform' and 'org' modes. The only mode-specific
 *     rendering is the budget field's hint text and the form heading.
 *   - All API calls go through `apiFetch` so X-Organization-Id is attached
 *     automatically (FW8 lesson — never use raw fetch in app code).
 *   - M1 Packet A: Two model dropdowns — "Default model" and "Complex model"
 *     per provider, matching the two-tier model resolution in the LLM config.
 *
 * Sven Q (2026-04-26): "Please do not copy components...REUSE THEM!"
 * This file is the answer.
 */

import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Button } from '@/components/ui/Button';
import { apiFetch } from '@/lib/api';
import { PROVIDER_LABELS, PROVIDER_ICONS } from './aiProviderConstants';
import { useOrgStore } from '@/stores/orgStore';

/**
 * Compute a short non-reversible FNV-1a hash of the input string.
 * Used to include a fingerprint of the API key in the React Query cache
 * key — so that typing a *different* key into the field triggers a new
 * fetch without exposing the plaintext key in queryKey logs / DevTools.
 */
function shortHash(str: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h.toString(16);
}

/**
 * AC-LLMCFG-1: Guard — returns false when a model string clearly belongs
 * to a different provider (prefix mismatch). Used to prevent cross-provider
 * contamination in the model dropdown extras.
 */
function modelBelongsToProvider(model: string, provider: string): boolean {
  const m = model.toLowerCase();
  if (provider === 'openai') return m.startsWith('gpt-') || m.startsWith('o1') || m.startsWith('o3') || m.startsWith('o4');
  if (provider === 'anthropic') return m.startsWith('claude-');
  if (provider === 'gemini') return m.startsWith('gemini-');
  return true;
}

// ─── Types ──────────────────────────────────────────────────────────────────

export interface ProviderInfo {
  provider: string;
  knownModels: string[];
  capabilities: {
    text: boolean;
    vision: boolean;
    structuredOutput: boolean;
    embeddings: boolean;
    webSearch: boolean;
  };
  // Other fields the catalog endpoint may include — kept loose so we don't
  // tightly couple to the catalog schema.
  [key: string]: unknown;
}

/** Initial values when editing an existing config. */
export interface EditableConfig {
  id: string;
  provider: string;
  modelPreference: string | null;
  /** Complex-tier model selection. Null = Provider Default. */
  modelComplex: string | null;
  baseUrl: string | null;
  monthlyBudget: number | null;
  enabled: boolean;
}

/** Payload shape submitted to the parent's onSubmit handler. */
export interface AIProviderFormData {
  provider: string;
  apiKey?: string;
  modelPreference: string | null;
  /** Complex-tier model selection. Null = Provider Default. */
  modelComplex: string | null;
  baseUrl: string | null;
  monthlyBudget: number | null;
  enabled: boolean;
}

export interface AIProviderFormSnapshot {
  provider: string;
  apiKey: string;
  model: string;
  modelComplex: string;
  baseUrl: string;
  budget: string;
  enabled: boolean;
}

export interface AIProviderFormProps {
  /** Determines small copy differences (budget hint text, form heading). */
  mode: 'platform' | 'org';

  /** Full provider catalog (from `/api/ai-config/providers`). */
  providers: ProviderInfo[];

  /** Subset of providers shown in the "new" dropdown. Equals `providers`
   *  minus the ones already configured. Ignored when `editingConfig` is set. */
  availableProviders: ProviderInfo[];

  /** When set, the form is in edit mode — provider is locked, API key is
   *  optional (leave blank to keep existing). */
  editingConfig: EditableConfig | null;

  /** Called on form submit with normalized payload. */
  onSubmit: (data: AIProviderFormData) => Promise<void> | void;

  /** Called on cancel. */
  onCancel: () => void;

  /** Show spinner state on the submit button. */
  isSaving: boolean;

  /**
   * When true, the built-in Cancel/Save action buttons at the bottom are not
   * rendered. Used when the form is hosted inside a FormSheet which provides
   * its own header-level actions.
   */
  hideActions?: boolean;

  /**
   * Called whenever the form state changes, with the current snapshot.
   * Used by the parent FormSheet for dirty-state tracking.
   */
  onSnapshotChange?: (snapshot: AIProviderFormSnapshot) => void;

  /**
   * Optional ref to the underlying <form> element — lets the parent trigger
   * submission via formRef.current?.requestSubmit() (e.g. from FormSheet's Save button).
   */
  formRef?: React.RefObject<HTMLFormElement | null>;
}

// ─── Component ──────────────────────────────────────────────────────────────

export function AIProviderForm({
  mode,
  providers,
  availableProviders,
  editingConfig,
  onSubmit,
  onCancel,
  isSaving,
  hideActions = false,
  onSnapshotChange,
  formRef,
}: AIProviderFormProps) {
  const isEditing = !!editingConfig;
  // Include the active org in the query key so a client-side org switch
  // invalidates the stale model list (item 4b).
  const activeOrgId = useOrgStore((s) => s.activeOrgId);

  // ─── Form state ───────────────────────────────────────────────────────────
  const [provider, setProvider] = useState(editingConfig?.provider ?? availableProviders[0]?.provider ?? 'openai');
  const [apiKey, setApiKey] = useState('');
  const [model, setModel] = useState(editingConfig?.modelPreference ?? '');
  const [modelComplex, setModelComplex] = useState(editingConfig?.modelComplex ?? '');
  const [baseUrl, setBaseUrl] = useState(editingConfig?.baseUrl ?? '');
  const [budget, setBudget] = useState(editingConfig?.monthlyBudget != null ? String(editingConfig.monthlyBudget) : '');
  const [enabled, setEnabled] = useState(editingConfig?.enabled ?? true);

  // Reset form when editingConfig changes (e.g. user opens edit on a different row)
  useEffect(() => {
    if (editingConfig) {
      setProvider(editingConfig.provider);
      setApiKey('');
      setModel(editingConfig.modelPreference ?? '');
      setModelComplex(editingConfig.modelComplex ?? '');
      setBaseUrl(editingConfig.baseUrl ?? '');
      setBudget(editingConfig.monthlyBudget != null ? String(editingConfig.monthlyBudget) : '');
      setEnabled(editingConfig.enabled);
    }
  }, [editingConfig]);

  // ─── Debounced API key for live model fetch ────────────────────────────────
  // We debounce so rapid typing doesn't fire a fetch on every keystroke.
  // The key itself is NOT put in the React Query key (security) — only whether
  // one is present, so the cache entry doesn't encode the plaintext key.
  const [debouncedApiKey, setDebouncedApiKey] = useState(apiKey);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedApiKey(apiKey), 400);
    return () => clearTimeout(t);
  }, [apiKey]);

  // ─── Dynamic models for the selected provider ─────────────────────────────
  // POST to /api/ai-config/models/{provider} — sends the entered key if present
  // so the user sees live models as they type a valid key.
  // Falls back to the static knownModels list on error or empty response.
  //
  // Query key design:
  //   (a) We include a short FNV-1a hash of the debounced key (not the key
  //       itself) so changing the key to a *different value* triggers a new
  //       fetch.  Using only a boolean `hasKey` meant switching from one valid
  //       key to another never refreshed the model list.
  //   (b) We include `activeOrgId` so switching orgs on the client side
  //       invalidates the cached list — each org's saved key may differ.
  const keyHash = debouncedApiKey.trim() ? shortHash(debouncedApiKey.trim()) : '';
  const { data: dynamicModels, isLoading: isLoadingModels } = useQuery<Array<{ id: string; name: string }>>({
    queryKey: ['ai-models', provider, keyHash, activeOrgId],
    queryFn: async () => {
      const body: Record<string, string> = {};
      if (debouncedApiKey.trim()) body.apiKey = debouncedApiKey.trim();
      const res = await apiFetch(`/api/ai-config/models/${provider}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!data.success) throw new Error(data.error?.message || 'Request failed');
      return Array.isArray(data.data) ? (data.data as Array<{ id: string; name: string }>) : [];
    },
    enabled: !!provider,
    staleTime: 5 * 60_000,
    retry: 1,
  });

  const selectedProviderInfo = providers.find((p) => p.provider === provider);

  /**
   * Final list of {id, name} model options — dynamic if available, otherwise
   * the static knownModels mapped to the same shape.
   *
   * BUG E1 fix: when editing, the saved model id (e.g. 'claude-sonnet-4-6')
   * may be a live model not present in KNOWN_MODELS or the cached curated list.
   * Guarantee it always appears as an option so the <select> renders the saved
   * value instead of falling back to "Provider Default".
   */
  const modelOptions: Array<{ id: string; name: string }> = useMemo(() => {
    const base: Array<{ id: string; name: string }> =
      dynamicModels && dynamicModels.length > 0
        ? dynamicModels
        : (selectedProviderInfo?.knownModels ?? []).map((m) => ({ id: m, name: m }));

    // AC-LLMCFG-1: Only prepend saved selections that belong to THIS provider.
    // Foreign models (e.g. gpt-4.1-nano on an Anthropic edit) must not appear.
    const ids = new Set(base.map((m) => m.id));
    const extras: Array<{ id: string; name: string }> = [];
    if (model && !ids.has(model) && modelBelongsToProvider(model, provider))
      extras.push({ id: model, name: model });
    if (modelComplex && !ids.has(modelComplex) && modelBelongsToProvider(modelComplex, provider))
      extras.push({ id: modelComplex, name: modelComplex });

    return extras.length > 0 ? [...extras, ...base] : base;
  }, [dynamicModels, selectedProviderInfo, model, modelComplex, provider]);

  // ─── Snapshot notifications for parent dirty-detection ───────────────────
  useEffect(() => {
    onSnapshotChange?.({ provider, apiKey, model, modelComplex, baseUrl, budget, enabled });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, apiKey, model, modelComplex, baseUrl, budget, enabled]);

  // ─── Submit handler ───────────────────────────────────────────────────────
  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload: AIProviderFormData = {
      provider,
      modelPreference: model || null,
      modelComplex: modelComplex || null,
      baseUrl: baseUrl || null,
      monthlyBudget: budget ? parseFloat(budget) : null,
      enabled,
    };
    if (apiKey) payload.apiKey = apiKey;
    void onSubmit(payload);
  }

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div data-testid="ai-provider-form" className={hideActions ? '' : 'bg-card border border-border rounded-lg p-6 mb-8'}>
      {/* BUG E2 fix: when editing, show provider identity so the user knows which
          provider they are configuring. The provider <select> is hidden on edit,
          so without this the form is anonymous. */}
      <div className="flex items-center gap-3 mb-4">
        <h2 className="text-lg font-semibold text-foreground">
          {isEditing
            ? `Edit ${PROVIDER_LABELS[provider] ?? provider} Provider`
            : (mode === 'platform' ? 'Add Platform AI Provider' : 'Add AI Provider')}
        </h2>
        {isEditing && (
          <span
            data-testid="editing-provider-badge"
            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-sm font-medium"
          >
            {PROVIDER_ICONS[provider] ?? ''} {PROVIDER_LABELS[provider] ?? provider}
          </span>
        )}
      </div>
      <form onSubmit={handleSubmit} className="space-y-4" ref={formRef}>
        {/* Provider select (locked when editing) */}
        {!isEditing && (
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Provider</label>
            <select
              value={provider}
              onChange={(e) => { setProvider(e.target.value); setModel(''); setModelComplex(''); }}
              className="w-full max-w-md border border-border rounded-lg px-3 py-2 bg-card text-foreground"
            >
              {availableProviders.map((p) => (
                <option key={p.provider} value={p.provider}>
                  {PROVIDER_LABELS[p.provider] || p.provider}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* API Key */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            API Key {isEditing && <span className="text-muted-foreground font-normal">(leave blank to keep current)</span>}
          </label>
          <input
            type="password"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            placeholder={isEditing ? '••••••••' : 'sk-...'}
            required={!isEditing}
            className="w-full max-w-md border border-border rounded-lg px-3 py-2 bg-card text-foreground font-mono text-sm"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Encrypted at rest with AES-256-GCM. Never logged or exposed in API responses.
          </p>
        </div>

        {/* Default model (model_preference) */}
        {/* U-3 W2: "Provider Default" option removed. Empty → null stored; card shows "— (uses Default)". */}
        <div data-testid="default-model-section">
          <label className="block text-sm font-medium text-foreground mb-1">
            Default model <span className="text-muted-foreground font-normal">(optional)</span>
            {isLoadingModels && (
              <span className="ml-2 text-xs text-muted-foreground">Loading models...</span>
            )}
          </label>
          <select
            data-testid="select-default-model"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            disabled={isLoadingModels}
            className="w-full max-w-md border border-border rounded-lg px-3 py-2 bg-card text-foreground disabled:opacity-50"
          >
            <option value="">Select model…</option>
            {modelOptions.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            Used for everyday AI tasks (e.g. drafts, summaries, routine suggestions).
          </p>
        </div>

        {/* Complex model (model_complex) */}
        {/* U-3 W2: "Provider Default" option removed. Empty → null stored; card shows "— (uses Default)". */}
        <div data-testid="complex-model-section">
          <label className="block text-sm font-medium text-foreground mb-1">
            Complex model <span className="text-muted-foreground font-normal">(optional)</span>
          </label>
          <select
            data-testid="select-complex-model"
            value={modelComplex}
            onChange={(e) => setModelComplex(e.target.value)}
            disabled={isLoadingModels}
            className="w-full max-w-md border border-border rounded-lg px-3 py-2 bg-card text-foreground disabled:opacity-50"
          >
            <option value="">Select model…</option>
            {modelOptions.map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            Used for reasoning-heavy tasks (e.g. decisions, moderation, persona consultations).
          </p>
          {!isLoadingModels && modelOptions.length === 0 && (
            <p className="text-xs text-muted-foreground mt-1">
              No models available for this provider yet.
            </p>
          )}
        </div>

        {/* Base URL (proxy) */}
        {provider === 'openai' && (
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">
              Base URL <span className="text-muted-foreground font-normal">(optional, for proxy)</span>
            </label>
            <input
              type="url"
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://your-proxy.example.com/v1"
              className="w-full max-w-lg border border-border rounded-lg px-3 py-2 bg-card text-foreground text-sm"
            />
          </div>
        )}

        {/* Monthly budget */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">
            Monthly Budget (USD) <span className="text-muted-foreground font-normal">(optional)</span>
          </label>
          <div className="flex items-center gap-2 max-w-xs">
            <span className="text-foreground">$</span>
            <input
              type="number"
              step="0.01"
              min="0"
              value={budget}
              onChange={(e) => setBudget(e.target.value)}
              placeholder={mode === 'platform' ? '500.00' : '50.00'}
              className="w-full border border-border rounded-lg px-3 py-2 bg-card text-foreground"
            />
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            {mode === 'platform'
              ? 'Platform-level budget. When exceeded, the platform config is skipped and env var fallback is used (if available).'
              : 'API calls will be blocked when budget is exceeded. Warnings at 80% and 90%.'}
          </p>
        </div>

        {/* Enabled toggle */}
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id={`ai-enabled-${mode}`}
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="w-4 h-4 rounded border-border"
          />
          <label htmlFor={`ai-enabled-${mode}`} className="text-sm text-foreground">
            {mode === 'platform'
              ? 'Enabled — use this as the platform default for organizations without their own config'
              : 'Enabled — use this provider for AI features'}
          </label>
        </div>

        {/* Actions — hidden when form is hosted inside a FormSheet (AC-ALX-7) */}
        {!hideActions && (
          <div className="flex items-center gap-3 pt-2">
            <Button type="submit" variant="primary" isLoading={isSaving}>
              {isEditing ? 'Save Changes' : 'Add Provider'}
            </Button>
            <Button type="button" variant="ghost" onClick={onCancel} disabled={isSaving}>
              Cancel
            </Button>
          </div>
        )}
      </form>
    </div>
  );
}
